import { useCallback } from 'react';
import { useToast } from 'hooks/useToast';

import { useAppSelector, useAppDispatch } from 'store/hooks/useStore';
import type { Customer } from 'types/customer';
import * as customerApi from 'api/customerApi';
import {
  listSelectors,
  listActions,
} from 'store/reducers/customerReducer/listSlice';

/**
 * 顧客一覧 store の実装をコンポーネントから隠すための中間層
 * @returns selector と action のラッパー
 */
export const useListStore = () => {
  const dispatch = useAppDispatch();
  const toast = useToast();

  const loading = useAppSelector(listSelectors.loading);
  const filteredCustomers = useAppSelector(listSelectors.filteredCustomers);
  const selectedCustomer = useAppSelector(listSelectors.selectedCustomer);
  const searchValue = useAppSelector(listSelectors.searchValue);
  const finalUpdateTime = useAppSelector(listSelectors.finalUpdateTime);

  const fetchCustomers = useCallback(async () => {
    dispatch(listActions.loadStarted());
    const result = await customerApi.fetchCustomers();
    if (result.ok) {
      dispatch(
        listActions.fetchCustomersSuccess({
          originalCustomers: result.value,
        }),
      );
    } else {
      toast({
        status: 'error',
        description: `顧客一覧の取得に失敗しました: ${result.err.message}`,
      });
    }
    dispatch(listActions.loadEnded());
  }, [dispatch, toast]);

  const selectCustomer = useCallback(
    (customer: Customer) => {
      dispatch(listActions.customerSelected({ customer }));
    },
    [dispatch],
  );

  const changeSearchValue = useCallback(
    (value: string) => {
      dispatch(listActions.searchValueChanged({ value }));
    },
    [dispatch],
  );

  const clearSelectedCustomer = useCallback(() => {
    dispatch(listActions.selectedCustomerCleared());
  }, [dispatch]);

  const changeCustomerStatus = useCallback(
    (status: string) => {
      dispatch(listActions.selectCustomerStatus({ status }));
    },
    [dispatch],
  );

  return {
    loading,
    filteredCustomers,
    selectedCustomer,
    searchValue,
    finalUpdateTime,
    fetchCustomers,
    changeSearchValue,
    selectCustomer,
    clearSelectedCustomer,
    changeCustomerStatus,
  };
};
